import type { Locale } from '@mfw/translations';

import { config } from '@mfw/translations';

import type { ApiTranslation } from '../schemas/translations.schema';

/**
 * What `getLabelFromTranslations` actually needs — and all it needs.
 *
 * It used to declare `UiTranslation`, the FORM schema, which has no
 * `description` at all; reading one was an index into a type without the key
 * (TS7053), and that is what the two `@ts-ignore`s here were hiding. Meanwhile
 * twelve call sites ask for exactly that property and pass ENTITY translations,
 * which do carry it — one of them casting to `UiTranslation[]` to get past the
 * mismatch.
 *
 * So the parameter, not the reading, was wrong. This says what the function
 * reads: a locale tag and two optional strings.
 */
export type TranslatableRow = {
  // The rows are autocomplete selections, so each entry carries a `label`
  // alongside the tag. Only `value` is read here; `label` is declared so an
  // inline row literal is not rejected as an excess property.
  locale?: ReadonlyArray<{ value: string; label?: string }> | null;
  description?: string | null;
  name?: string | null;
};

/**
 * Pass entity translations and locale to get the translated label.
 * TODO Make uniq zod schema for translation object and use it here and in categories, tags, roles, configurables etc.
 */
export function getLabelFromTranslations(
  locale: Locale,
  translations?: ReadonlyArray<TranslatableRow>,
  options?: { property?: 'description' | 'name' },
): string {
  if (!translations || translations.length === 0) return '';
  const property = options?.property ?? 'name';
  // Only the FIRST locale entry of a row is consulted — unchanged from
  // before, and the reason a row tagged with several locales is matched by
  // just one of them.
  const forLocale = (wanted: string) =>
    translations.find(
      (translation) => translation.locale?.[0]?.value === wanted,
    )?.[property];

  // Roles and content can have only the default-language translation;
  // content can also exist only in a language the UI does not speak (V1
  // content in hr, de, …), so the first filled-in row is the last resort.
  return (
    forLocale(locale) ||
    forLocale(config.defaultLocale) ||
    translations.find((translation) => translation[property])?.[property] ||
    ''
  );
}

export function getTranslation(
  locale: Locale,
  translations: Array<ApiTranslation> = [],
) {
  const forLocale = (wanted: string) =>
    translations.find((translation) => translation.locale === wanted)?.name;
  return (
    forLocale(locale) ||
    forLocale(config.defaultLocale) ||
    translations.find((translation) => translation.name)?.name ||
    ''
  );
}
